import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { KycWorkspaceService } from '../tokens/kyc-workspace.token';
import {
  KycTask,
  KycCompletenessData,
  KycPsd2ConnectionStatus,
  KycModificationLoopData,
  ReclaimRequest,
  ReclaimResponse,
  WorkflowTaskDTO,
  BackendModificationRequestDTO,
  ApplicantModificationSubmitDTO,
  CsContactResultDTO,
  FlowControlSettingDTO,
  FlowControlToggleRequestDTO,
  DocumentUploadDTO,
  OnboardingStageHistoryDTO,
  DocumentReviewRequestDTO,
  KycDossier,
} from '../models/kyc.model';
import { ExecutionTask } from '../models/task.model';
import { API_PATHS } from '../config/api-paths';
import { extractBackendErrorMessage } from '../util/backend-error';

// ── Mapper: ExecutionTask → KycTask ───────────────────────────────────────────

/**
 * Maps a unified `ExecutionTask` (returned by POST /api/tasks/{taskId}/claim)
 * to the portal's `KycTask` model.
 *
 * Heuristic fields (priority, slaClass, sla, menu, filters, tag, entityType, stage)
 * are set to safe defaults so component code stays unchanged.
 * Used by `claimTask` (officer-workspace, risk-rating pages).
 */
function mapExecutionTaskToKycTask(task: ExecutionTask): KycTask {
  const claimed = task.claimedByLogin !== null && task.claimedByLogin !== undefined;
  return {
    id: task.taskId,
    entity: task.partyDisplayName ?? task.cui ?? String(task.applicationId),
    entityType: 'SME',
    tag: 'customer',
    stage: task.stage ?? 'Stage 7',
    priority: 'Medium',
    sla: '—',
    slaClass: 'sla-orange',
    flow: (task.flow as 'A' | 'B') ?? 'B',
    status: 'In Progress',
    menu: task.track === 'TRACK2' ? 'sme-track2' : 'sme-track1',
    filters: ['all'],
    claimed,
    processInstanceId: task.processInstanceId ?? null,
  };
}

// ── Backend onboarding-application DTO (minimal shape needed here) ────────────

interface BackendOnboardingFormDTO {
  id?: number;
  applicationReference?: string | null;
  status?: string;
  currentStage?: string;
  track?: number | null;
  companyInfo?: {
    legalName?: string;
    cui?: string;
    tradeRegistryNumber?: string;
    legalForm?: string;
    naceCode?: string;
    // Registered-address fields (EM-864 — surfaced on the completeness screen).
    street?: string | null;
    streetExtra?: string | null;
    city?: string | null;
    county?: string | null;
    country?: string | null;
    postalCode?: string | null;
    representativeFullName?: string;
    representativePosition?: string;
    incorporationDate?: string | null;
    primaryEmail?: string | null;
    primaryPhone?: string | null;
  };
  // EM-864: the wire DTO carries the full financials block — the portal type
  // previously under-declared it (only revenueLastFy / numberOfEmployees /
  // yearsInOperation), discarding the prior-FY revenue, employee band, expected
  // monthly volume and currency the completeness screen must show.
  financials?: {
    revenueLastFy?: number | null;
    revenuePriorFy?: number | null;
    numberOfEmployees?: number | null;
    employeeCountBand?: string | null;
    yearsInOperation?: number | null;
    expectedMonthlyVolume?: number | null;
    currency?: string | null;
  };
  // EM-864: the financing request block (product, amount, purpose, tenor, urgency)
  // is already returned on the wire — it was simply never declared here, so the
  // completeness screen could not render it.
  financingRequest?: {
    product?: string | null;
    requestedAmount?: number | null;
    currency?: string | null;
    financingPurpose?: string | null;
    financingPurposeOther?: string | null;
    desiredTenorMonths?: number | null;
    urgency?: string | null;
  };
  psd2ConnectionState?: string | null;
  // EM-1171: the aggregate DTO carries the application type and the lender-specific
  // business-details block for every LENDER_* application (`populateLenderSection`).
  // They were simply never declared here — the same omission EM-864 fixed for
  // `financials` / `financingRequest`. Shapes mirror `onboarding-application.service.ts`.
  applicationType?: string | null;
  isNbfi?: boolean;
  lenderType?: string | null;   // LenderType enum name: NBFI | SRL | SA
  lenderBusinessDetails?: {
    lenderType?: string | null;
    numberOfEmployees?: number | null;
    availableExposureAmount?: number | null;
    investmentFocus?: string | null;
  } | null;
  // EM-1273: the aggregate DTO also carries the E-D business-details block and the
  // §3.2.7 auto-retrieval gap list for every ED_SRL_SA application (`populateEdSection`).
  // Same omission as EM-864 / EM-1171 — served on the wire, never declared here.
  // (`industrySector` / `distributionFocus` are now persisted and echoed back by
  // `populateEdSection` — EM-1271, the E-D twin of the lender's EM-1280 `investmentFocus`.)
  edBusinessDetails?: {
    supplyChainRole?: string | null;
    numberOfEmployees?: number | null;
    industrySector?: string | null;
    distributionFocus?: string | null;
  } | null;
  autoRetrievalGaps?: string[] | null;
  documents?: Array<{
    id?: number;
    documentType?: string;
    originalFilename?: string;
    uploadedAt?: string;
  }>;
}

// ── REST service ──────────────────────────────────────────────────────────────

/**
 * REST-backed implementation of `KycWorkspaceService`.
 *
 * All calls are authenticated via the portal's `authInterceptor` (signed-in
 * KYC officer's Keycloak bearer token). No anonymous-auth path is used.
 *
 * Queue and task-management calls go through the unified `TaskResource`
 * (`/api/tasks`). The removed `/api/kyc-review/*` surface was deleted in the
 * Phase-5 TaskResource consolidation (Rule 14).
 */
@Injectable()
export class KycWorkspaceRestService implements KycWorkspaceService {
  private readonly http = inject(HttpClient);

  // ── taskId → applicationId resolution cache ──────────────────────────────────

  /**
   * Cache maps a Flowable taskId → JPA applicationId derived from the role
   * queue response.  Populated whenever `resolveExecutionTask` is called.
   */
  private taskToAppCache = new Map<string, number>();

  /**
   * Per-applicationId form cache.  Short-lived (30s TTL) so multiple sub-calls
   * from the same task view don't hammer the backend.
   */
  private formCache = new Map<number, { form: BackendOnboardingFormDTO; expiresAt: number }>();

  /**
   * Real: POST /api/tasks/{taskId}/claim → ExecutionTask → KycTask
   *
   * The unified claim endpoint handles every task type; the old per-task
   * routing via `taskTypeMap` (Stage 3 KYC review vs Stage 6 financial
   * verification) is removed along with the deleted backend endpoints.
   */
  claimTask(taskId: string): Observable<KycTask> {
    return this.http.post<ExecutionTask>(
      `${API_PATHS.EMBEDDED_API_BASE}/tasks/${taskId}/claim`,
      null,
    ).pipe(
      map(mapExecutionTaskToKycTask),
    );
  }

  /**
   * Reclaim = claim the task via the unified claim endpoint.
   * Semantically: the officer re-claims a task that was previously unclaimed
   * during the modification-loop.
   * TODO(reclaim-notes): expose notes field on the claim endpoint.
   */
  reclaimTask(req: ReclaimRequest): Observable<ReclaimResponse> {
    return this.http.post<ExecutionTask>(
      `${API_PATHS.EMBEDDED_API_BASE}/tasks/${req.taskId}/claim`,
      null,
    ).pipe(
      map(() => ({ success: true as const })),
      catchError((err: HttpErrorResponse) => {
        const msg = extractBackendErrorMessage(err, 'errors.kyc.reclaimFailed');
        return of({ success: false as const, errorCode: msg });
      }),
    );
  }

  // ── Completeness Check (EM-44) ────────────────────────────────────────────────

  /**
   * Completeness data built from REST calls (forkJoin):
   *   1. GET /api/tasks/{taskId} → resolves applicationId (+ party/cui fallbacks) from taskId
   *   2. GET /api/onboarding-application/{applicationId}/documents → DocumentUploadDTO[]
   *   3. GET /api/onboarding-application/{applicationId}/stage-history → OnboardingStageHistoryDTO[]
   *
   * Also fetches the base form for appSummary (legalName, cui, track, stage).
   *
   * The taskId → applicationId mapping goes through the consolidated TaskResource
   * (`GET /api/tasks/{taskId}`); the legacy `/api/kyc-review/queue` surface was
   * removed in the Phase-5 TaskResource consolidation (Rule 14).
   *
   * Mismatches and discrepancyNotes are deferred — no backend endpoint yet.
   */
  getCompletenessData(taskId: string): Observable<KycCompletenessData> {
    return this.resolveExecutionTask(taskId).pipe(
      switchMap(task => {
        const appId = task?.applicationId ?? this.taskToAppCache.get(taskId);
        if (appId === undefined || appId === null) {
          return throwError(() => ({ code: 'errors.kyc.taskNotFound' }));
        }
        return forkJoin({
          form: this.fetchForm(appId),
          documents: this.http.get<DocumentUploadDTO[]>(
            `${API_PATHS.ONBOARDING_APPLICATION}/${appId}/documents`,
          ),
          stageHistory: this.http.get<OnboardingStageHistoryDTO[]>(
            `${API_PATHS.ONBOARDING_APPLICATION}/${appId}/stage-history`,
          ),
          // EM-864: UBO + consents live only on the task dossier, not the form DTO.
          // Best-effort — a dossier failure must NOT blank the whole completeness
          // screen, so degrade to null and let the form-sourced data render (Rule 09).
          dossier: this.http.get<KycDossier>(
            `${API_PATHS.EMBEDDED_API_BASE}/tasks/${taskId}/dossier`,
          ).pipe(catchError(() => of<KycDossier | null>(null))),
        }).pipe(
          map(({ form, documents, stageHistory, dossier }) =>
            buildCompletenessData(taskId, appId, form, task, documents, stageHistory, dossier),
          ),
        );
      }),
      catchError((err: HttpErrorResponse) => {
        const msg = extractBackendErrorMessage(err, 'errors.kyc.completenessLoadFailed');
        return throwError(() => ({ code: msg }));
      }),
    );
  }

  /**
   * Review (verify or flag) a document.
   * Real: PUT /api/onboarding-application/{applicationId}/documents/{documentId}/review
   */
  verifyDocument(
    applicationId: number,
    documentId: string | number,
    reviewStatus: 'VERIFIED' | 'REQUIRES_MODIFICATION',
  ): Observable<DocumentUploadDTO> {
    const body: DocumentReviewRequestDTO = { reviewStatus };
    return this.http.put<DocumentUploadDTO>(
      `${API_PATHS.ONBOARDING_APPLICATION}/${applicationId}/documents/${documentId}/review`,
      body,
    ).pipe(
      catchError((err: HttpErrorResponse) => {
        const msg = extractBackendErrorMessage(err, 'errors.kyc.docReviewFailed');
        return throwError(() => ({ code: msg }));
      }),
    );
  }

  /**
   * Fetches a document's binary content as a Blob (authenticated — the auth interceptor attaches the
   * bearer token, so this cannot be an `<iframe src>`). The caller turns it into an object URL.
   * Real: GET /api/onboarding-application/{applicationId}/documents/{documentId}/content
   */
  getDocumentContent(applicationId: number, documentId: string | number): Observable<Blob> {
    return this.http.get(
      `${API_PATHS.ONBOARDING_APPLICATION}/${applicationId}/documents/${documentId}/content`,
      { responseType: 'blob' },
    );
  }

  // ── PSD2 Connectivity (EM-45) ─────────────────────────────────────────────────

  /**
   * PSD2 connection status synthesized from GET /api/onboarding-application/{applicationId}.
   *
   * taskId → applicationId resolved via GET /api/tasks/{taskId} (unified TaskResource).
   * Maps form.psd2ConnectionState → aispStatus.
   * Fields without backend source (retryCount, errorLog, auditTrail, stageTimeline)
   * return safe defaults / empty arrays.
   * TODO(psd2-connectivity-endpoint): expose PSD2 error log from backend.
   */
  getPsd2ConnectionStatus(taskId: string): Observable<KycPsd2ConnectionStatus> {
    return this.resolveAndFetchForm(taskId).pipe(
      map(({ form, task }) => buildPsd2ConnectionStatus(taskId, form, task)),
      catchError((err: HttpErrorResponse) => {
        const msg = extractBackendErrorMessage(err, 'errors.kyc.psd2StatusLoadFailed');
        return throwError(() => ({ code: msg }));
      }),
    );
  }

  // ── Modification Loop (EM-46) ─────────────────────────────────────────────────

  /**
   * Modification loop data from GET /api/sme-modification/{taskId}/request.
   *
   * taskId → ExecutionTask resolved via GET /api/tasks/{taskId} (unified TaskResource),
   * replacing the removed `/api/kyc-review/queue` scan (Phase-5 Rule 14).
   * Fields without a backend source (communicationTrail, auditTrail, stageTimeline)
   * return empty arrays.
   * TODO(mod-loop-endpoint): expose communication trail + audit from backend.
   */
  getModificationLoopData(taskId: string): Observable<KycModificationLoopData> {
    return this.http.get<BackendModificationRequestDTO>(
      `${API_PATHS.APPLICANT_MODIFICATION}/${taskId}/request`,
    ).pipe(
      switchMap(modReq => this.resolveExecutionTask(taskId).pipe(
        map(task => buildModificationLoopData(taskId, modReq, task)),
      )),
      catchError((err: HttpErrorResponse) => {
        const msg = extractBackendErrorMessage(err, 'errors.kyc.modLoopLoadFailed');
        return throwError(() => ({ code: msg }));
      }),
    );
  }

  // ── PSD2 Resolution (EM-47) ───────────────────────────────────────────────────

  // ── SME modification loop REST methods (EM-42) ────────────────────────────────

  /** Real: GET /api/sme-modification/{taskId}/request */
  getModificationRequest(taskId: string): Observable<BackendModificationRequestDTO> {
    return this.http.get<BackendModificationRequestDTO>(
      `${API_PATHS.APPLICANT_MODIFICATION}/${taskId}/request`,
    );
  }

  /** Real: PUT /api/sme-modification/{taskId}/save-request */
  saveModificationRequest(taskId: string, fields: string, requestText: string): Observable<WorkflowTaskDTO> {
    const params = new HttpParams()
      .set('fields', fields)
      .set('requestText', requestText);
    return this.http.put<WorkflowTaskDTO>(
      `${API_PATHS.APPLICANT_MODIFICATION}/${taskId}/save-request`,
      null,
      { params },
    );
  }

  /** Real: PUT /api/sme-modification/{taskId}/submit */
  submitSmeModification(req: ApplicantModificationSubmitDTO): Observable<WorkflowTaskDTO[]> {
    return this.http.put<WorkflowTaskDTO[]>(
      `${API_PATHS.APPLICANT_MODIFICATION}/${req.taskId}/submit`,
      req,
    );
  }

  /** Real: PUT /api/sme-modification/cs-contact-result */
  recordCsContactResult(req: CsContactResultDTO): Observable<void> {
    return this.http.put<void>(
      `${API_PATHS.APPLICANT_MODIFICATION}/cs-contact-result`,
      req,
    );
  }

  // ── Flow Control (EM-43) ──────────────────────────────────────────────────────

  /** Real: GET /api/flow-control */
  getFlowControlSettings(): Observable<FlowControlSettingDTO[]> {
    return this.http.get<FlowControlSettingDTO[]>(API_PATHS.FLOW_CONTROL);
  }

  /** Real: GET /api/flow-control/{channel} */
  getFlowControlSetting(channel: string): Observable<FlowControlSettingDTO> {
    return this.http.get<FlowControlSettingDTO>(`${API_PATHS.FLOW_CONTROL}/${channel}`);
  }

  /** Real: PUT /api/flow-control/{channel}/toggle */
  toggleFlowControl(channel: string, req: FlowControlToggleRequestDTO): Observable<FlowControlSettingDTO> {
    return this.http.put<FlowControlSettingDTO>(
      `${API_PATHS.FLOW_CONTROL}/${channel}/toggle`,
      { ...req, channel },
    );
  }

  // ── Private helpers ───────────────────────────────────────────────────────────

  /**
   * Resolve a Flowable taskId to its unified task summary via the consolidated
   * TaskResource (`GET /api/tasks/{taskId}`), caching the taskId → applicationId
   * mapping. Used by completeness, psd2-connectivity, modification-loop, and
   * psd2-resolution pages; replaces the removed `/api/kyc-review/queue` scan
   * (Phase-5 TaskResource consolidation, Rule 14).
   */
  private resolveExecutionTask(taskId: string): Observable<ExecutionTask> {
    return this.http.get<ExecutionTask>(`${API_PATHS.EMBEDDED_API_BASE}/tasks/${taskId}`).pipe(
      map(task => {
        if (task?.applicationId !== undefined && task?.applicationId !== null) {
          this.taskToAppCache.set(taskId, task.applicationId);
        }
        return task;
      }),
    );
  }

  /**
   * Fetch the onboarding-application form DTO, using a 30-second TTL cache
   * to prevent multiple round-trips for the same applicationId within a
   * single task-view lifecycle.
   */
  private fetchForm(applicationId: number): Observable<BackendOnboardingFormDTO> {
    const now = Date.now();
    const cached = this.formCache.get(applicationId);
    if (cached && cached.expiresAt > now) {
      return new Observable<BackendOnboardingFormDTO>(sub => {
        sub.next(cached.form);
        sub.complete();
      });
    }
    return this.http.get<BackendOnboardingFormDTO>(
      `${API_PATHS.ONBOARDING_APPLICATION}/${applicationId}`,
    ).pipe(
      map(form => {
        this.formCache.set(applicationId, { form, expiresAt: now + 30_000 });
        return form;
      }),
    );
  }

  /**
   * Convenience: resolve the ExecutionTask (taskId → applicationId + party fields)
   * and then fetch the onboarding-application form in one chain.
   *
   * Used by getPsd2ConnectionStatus.
   * Replaces the old `resolveAndFetchForm` that scanned `/api/kyc-review/queue`
   * (deleted in Phase-5 Rule 14). Now delegates to `resolveExecutionTask`.
   */
  private resolveAndFetchForm(taskId: string): Observable<{
    form: BackendOnboardingFormDTO;
    task: ExecutionTask | null;
  }> {
    return this.resolveExecutionTask(taskId).pipe(
      switchMap(task => {
        const appId = task?.applicationId ?? this.taskToAppCache.get(taskId);
        if (appId === undefined) {
          return throwError(() => ({ code: 'errors.kyc.taskNotFound' }));
        }
        return this.fetchForm(appId).pipe(
          map(form => ({ form, task })),
        );
      }),
    );
  }
}

// ── Synthesis helpers — convert backend DTO to portal display models ──────────
// The dossier is now served by `GET /api/tasks/{taskId}/dossier` (structured
// DTO via the TASKS token). The remaining synthesizers below back the standalone
// workpages (completeness-check, psd2-connectivity, modification-loop,
// psd2-resolution), which still derive from the onboarding-application aggregate
// pending their own purpose-built endpoints.

function buildCompletenessData(
  taskId: string,
  applicationId: number,
  form: BackendOnboardingFormDTO,
  task: ExecutionTask | null,
  documents: DocumentUploadDTO[],
  stageHistory: OnboardingStageHistoryDTO[],
  dossier: KycDossier | null,
): import('../models/kyc.model').KycCompletenessData {
  const legalName = form.companyInfo?.legalName ?? task?.partyDisplayName ?? '—';
  const cui = form.companyInfo?.cui ?? task?.cui ?? '—';
  const appRef = form.applicationReference ?? task?.applicationReference ?? '—';
  const track = form.track ?? 1;

  // OnboardingStage enum (backend) → onboarding stage number (1..8), in workflow order.
  // currentStage / stage-history rows carry these enum names, NOT "STAGE_N".
  const ONBOARDING_STAGE_ORDER = [
    'REGISTRATION',           // Stage 1
    'APPLICATION',            // Stage 2
    'COMPLETENESS_CHECK',     // Stage 3
    'BLACKLIST_SCREENING',    // Stage 4
    'CIC_DATA_CHECK',         // Stage 5
    'FINANCIAL_VERIFICATION', // Stage 6
    'RISK_RATING',            // Stage 7
    'CONTRACT_ACTIVATION',    // Stage 8
  ];
  const currentStageIdx = ONBOARDING_STAGE_ORDER.indexOf(form.currentStage ?? '');
  const stageNum = currentStageIdx >= 0 ? currentStageIdx + 1 : 3;

  // Map DocumentUploadDTO → KycDocument
  const kycDocuments: import('../models/kyc.model').KycDocument[] = documents.map(d => ({
    id: String(d.id),
    title: d.originalFilename || d.documentType || 'Document',
    url: null,  // content is fetched on demand as an authenticated blob (see getDocumentContent)
    status: mapDocReviewStatus(d.reviewStatus),
    // EM-1225: the required-document slot this file was submitted against — the officer's
    // primary label. Translated at the UI boundary by `documentTypeLabel()`.
    documentType: d.documentType ?? '',
    mimeType: d.mimeType ?? null,
    // "Supplementary — E-D Provided" tag (BRS Ch.3 §3.2.4) — surfaced in the review dossier.
    supplementary: d.isSupplementary === true,
  }));

  // Map stage-history rows → KycStageTimelineEntry.
  // Latest history timestamp per stage (max occurredAt), for display.
  const timeByStage = new Map<string, string>();
  for (const h of stageHistory) {
    const prev = timeByStage.get(h.stage);
    if (!prev || h.occurredAt > prev) timeByStage.set(h.stage, h.occurredAt);
  }
  // Drive completed/active/pending from the application's current stage position: a stage before
  // the current one is completed, the current one is active, later ones are pending. (Not every
  // stage writes a history row, so position — not history presence — is the source of truth.)
  const stageTimeline: import('../models/kyc.model').KycStageTimelineEntry[] =
    ONBOARDING_STAGE_ORDER.map((key, idx) => {
      let status: 'completed' | 'active' | 'pending';
      if (currentStageIdx < 0) {
        status = timeByStage.has(key) ? 'completed' : 'pending';
      } else if (idx < currentStageIdx) {
        status = 'completed';
      } else if (idx === currentStageIdx) {
        status = 'active';
      } else {
        status = 'pending';
      }
      return {
        stage: `Stage ${idx + 1}`,
        status,
        time: timeByStage.get(key),
      };
    });

  // Map stage-history rows → KycAuditEntry
  const auditTrail: import('../models/kyc.model').KycAuditEntry[] = stageHistory.map(h => ({
    time: h.occurredAt,
    action: `${h.stage}: ${h.previousStatus ?? '—'} → ${h.newStatus}${h.reasonText ? ` (${h.reasonText})` : ''}`,
    user: h.actorUserLogin ?? (h.systemTriggered ? 'system' : '—'),
  }));

  return {
    taskId,
    applicationId,
    appSummary: {
      appId: appRef,
      entity: legalName,
      cui,
      track: `Track ${track}`,
      stage: `Stage ${stageNum}`,
      flow: 'B',
      sla: '—',
      slaClass: 'sla-orange',
    },
    documents: kycDocuments,
    // Deferred — no backend endpoint for mismatches yet.
    mismatches: [],
    // Deferred — no backend endpoint for discrepancy notes yet.
    discrepancyNotes: [],
    auditTrail,
    stageTimeline,
    // EM-1171: the entity type drives the lender-aware checklist / panels. The form
    // aggregate is authoritative; the task summary is the fallback for the rare case
    // where the aggregate omits it.
    applicationType: form.applicationType ?? task?.applicationType ?? null,
    // EM-864: the submitted data the officer certifies (financing request, company,
    // financials, PSD2 state from the form; UBO + consents from the dossier).
    submittedData: buildSubmittedData(form, dossier),
  };
}

/**
 * EM-864: assemble the "Submitted Application Data" block the completeness screen
 * renders. Form-sourced blocks (financingRequest / company / financials /
 * psd2ConnectionState) always populate; dossier-sourced blocks (beneficialOwners /
 * consents) degrade to `[]` when the dossier could not be loaded.
 */
function buildSubmittedData(
  form: BackendOnboardingFormDTO,
  dossier: KycDossier | null,
): import('../models/kyc.model').KycCompletenessData['submittedData'] {
  const fr = form.financingRequest;
  const ci = form.companyInfo;
  const fin = form.financials;
  const lbd = form.lenderBusinessDetails;
  const ebd = form.edBusinessDetails;

  // Compose the registered address from its parts, dropping blank segments.
  const address = ci
    ? [ci.street, ci.streetExtra, ci.city, ci.county, ci.postalCode, ci.country]
        .map(p => (p ?? '').trim())
        .filter(p => p.length > 0)
        .join(', ') || null
    : null;

  return {
    financingRequest: fr
      ? {
          product: fr.product ?? null,
          requestedAmount: fr.requestedAmount ?? null,
          currency: fr.currency ?? null,
          financingPurpose: fr.financingPurpose ?? null,
          financingPurposeOther: fr.financingPurposeOther ?? null,
          desiredTenorMonths: fr.desiredTenorMonths ?? null,
          urgency: fr.urgency ?? null,
        }
      : null,
    company: ci
      ? {
          legalName: ci.legalName ?? null,
          cui: ci.cui ?? null,
          tradeRegistryNumber: ci.tradeRegistryNumber ?? null,
          legalForm: ci.legalForm ?? null,
          naceCode: ci.naceCode ?? null,
          address,
          representativeFullName: ci.representativeFullName ?? null,
          representativePosition: ci.representativePosition ?? null,
          incorporationDate: ci.incorporationDate ?? null,
          primaryEmail: ci.primaryEmail ?? null,
          primaryPhone: ci.primaryPhone ?? null,
        }
      : null,
    financials: fin
      ? {
          revenueLastFy: fin.revenueLastFy ?? null,
          revenuePriorFy: fin.revenuePriorFy ?? null,
          numberOfEmployees: fin.numberOfEmployees ?? null,
          employeeCountBand: fin.employeeCountBand ?? null,
          yearsInOperation: fin.yearsInOperation ?? null,
          expectedMonthlyVolume: fin.expectedMonthlyVolume ?? null,
          currency: fin.currency ?? null,
        }
      : null,
    // EM-1171: lender business details (BRS §2.2.2). Null for every non-lender
    // application — the page renders this block only for a Lender dossier.
    lenderBusinessDetails: lbd
      ? {
          lenderType: lbd.lenderType ?? form.lenderType ?? null,
          numberOfEmployees: lbd.numberOfEmployees ?? null,
          availableExposureAmount: lbd.availableExposureAmount ?? null,
          investmentFocus: lbd.investmentFocus ?? null,
        }
      : null,
    // EM-1273: E-D business details (BRS Ch.3 §3.2.2). Null for every non-E-D
    // application — the page renders this block only for an E-D dossier.
    edBusinessDetails: ebd
      ? {
          supplyChainRole: ebd.supplyChainRole ?? null,
          numberOfEmployees: ebd.numberOfEmployees ?? null,
          // EM-1271: persisted since the Stage-2 free-text fields got their columns —
          // carried through exactly like the lender's `investmentFocus` (EM-1280) above.
          industrySector: ebd.industrySector ?? null,
          distributionFocus: ebd.distributionFocus ?? null,
        }
      : null,
    // EM-1273: §3.2.7 auto-retrieval gaps — the fields the officer must verify by hand
    // before certifying the auto-retrieved data. Empty for non-E-D applications.
    autoRetrievalGaps: form.autoRetrievalGaps ?? [],
    psd2ConnectionState: form.psd2ConnectionState ?? null,
    // EM-1208 — dossier-sourced proof that AISP data was really retrieved; `null` when the
    // dossier is unavailable, in which case the page falls back to the raw state label.
    aispDataRetrieved: dossier?.psd2?.aispDataRetrieved ?? null,
    // Dossier-sourced — empty when the dossier was unavailable (best-effort).
    beneficialOwners: dossier?.kycIdentity?.beneficialOwners ?? [],
    consents: dossier?.psd2?.consents ?? [],
  };
}

function mapDocReviewStatus(
  reviewStatus: DocumentUploadDTO['reviewStatus'],
): import('../models/kyc.model').KycDocument['status'] {
  switch (reviewStatus) {
    case 'VERIFIED': return 'verified';
    case 'REQUIRES_MODIFICATION':
    case 'REJECTED': return 'flag';
    case 'PENDING':
    default: return 'pending';
  }
}

function buildPsd2ConnectionStatus(
  taskId: string,
  form: BackendOnboardingFormDTO,
  task: ExecutionTask | null,
): import('../models/kyc.model').KycPsd2ConnectionStatus {
  const legalName = form.companyInfo?.legalName ?? task?.partyDisplayName ?? '—';
  const appRef = form.applicationReference ?? task?.applicationReference ?? '—';
  const track = form.track ?? 1;
  const stageMatch = (form.currentStage ?? '').match(/(\d+)$/);
  const stageNum = stageMatch ? parseInt(stageMatch[1], 10) : 3;

  const psd2State = form.psd2ConnectionState ?? null;
  const aispStatus: import('../models/kyc.model').Psd2AispStatus = mapPsd2State(psd2State);

  return {
    taskId,
    aispStatus,
    // TODO(psd2-connectivity-endpoint): backend does not expose retry count yet.
    retryCount: 0,
    bankProvider: '—',
    bankProviderNote: '—',
    slaLabel: '—',
    slaRemaining: '—',
    // TODO(psd2-connectivity-endpoint): backend does not expose error log yet.
    errorLog: [],
    appSummary: {
      appId: appRef,
      entity: legalName,
      track: `Track ${track}`,
      stage: `Stage ${stageNum}`,
      flow: 'B',
      sla: '—',
      slaClass: 'sla-orange',
    },
    // TODO(psd2-connectivity-endpoint): backend does not expose audit trail yet.
    auditTrail: [],
    // TODO(psd2-connectivity-endpoint): backend does not expose stage timeline yet.
    stageTimeline: [],
  };
}

function mapPsd2State(state: string | null): import('../models/kyc.model').Psd2AispStatus {
  switch (state) {
    case 'AISP_DATA_RETRIEVED': return 'Connected';
    case 'FAILED':
    case 'AISP_FAILED': return 'Failed';
    case 'PENDING':
    case 'AISP_PENDING': return 'Pending';
    case 'EXPIRED': return 'Expired';
    default: return 'Pending';
  }
}

function buildModificationLoopData(
  taskId: string,
  modReq: BackendModificationRequestDTO,
  task: ExecutionTask | null,
): import('../models/kyc.model').KycModificationLoopData {
  const legalName = task?.partyDisplayName ?? '—';
  const appId = task?.applicationReference ?? (task ? String(task.applicationId) : '—');

  // Parse fields string into individual correction field descriptions.
  const fieldsStr = modReq.fields ?? '';
  const correctionFields = fieldsStr.length > 0
    ? fieldsStr.split(',').map(f => ({ description: f.trim() }))
    : [];

  return {
    taskId,
    // Backend does not expose CS contact status; default to 'no' until available.
    // TODO(mod-loop-endpoint): expose CS contact status from backend.
    csContacted: 'no',
    csContactedAt: '—',
    smeNotified: 'none',
    smeNotifiedAt: '—',
    smeResponse: 'pending',
    autoCloseCountdown: '—',
    autoCloseTimerLabel: '—',
    correctionFields,
    // TODO(mod-loop-endpoint): backend does not expose communication trail yet.
    communicationTrail: [],
    appSummary: {
      appId,
      entity: legalName,
      track: '—',
      stage: 'Stage 3',
      flow: 'B',
      sla: '—',
      slaClass: 'sla-orange',
    },
    // TODO(mod-loop-endpoint): backend does not expose audit trail yet.
    auditTrail: [],
    // TODO(mod-loop-endpoint): backend does not expose stage timeline yet.
    stageTimeline: [],
  };
}

